import {
  createPanelFrame,
  resolveTitle,
  createMetric,
  formatPercent,
  formatNumber,
  toNumber
} from './panel-utils.js';
import { createSvg, clearSvg, createSvgEl } from './chart-utils.js';

const DEFAULT_COLUMNS = [
  { name: 'age', type: 'int', missing: 0.01, distinct: 74, stats: { min: 18, max: 92, mean: 41 }, histogram: [2, 5, 8, 12, 10, 7, 4] },
  { name: 'session_length', type: 'float', missing: 0.04, distinct: 421, stats: { min: 0.2, max: 38.4, mean: 6.1 }, histogram: [1, 4, 6, 9, 13, 9, 5, 2] },
  { name: 'country', type: 'category', missing: 0.0, distinct: 32, stats: { top: 'US', topShare: 0.41 } },
  { name: 'device_type', type: 'category', missing: 0.0, distinct: 5, stats: { top: 'mobile', topShare: 0.62 } }
];

export function render({ node, api }) {
  const { root, body, titleEl, metaEl } = createPanelFrame(node, 'Dataset Profiler');
  const metrics = document.createElement('div');
  metrics.className = 'ew-metric-grid';

  const list = document.createElement('div');
  list.className = 'ew-list';

  const detail = document.createElement('div');
  detail.className = 'ew-detail';

  const histWrap = document.createElement('div');
  histWrap.className = 'ew-mini-chart';
  const histSvg = createSvg(320, 80);
  histWrap.appendChild(histSvg);

  detail.appendChild(histWrap);
  body.append(metrics, detail, list);

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Dataset Profiler');
    const columns = Array.isArray(next?.data?.columns || next?.props?.columns)
      ? (next.data?.columns || next.props?.columns)
      : DEFAULT_COLUMNS;
    const rows = toNumber(next?.data?.rows ?? next?.props?.rows, 120000);
    const totalColumns = columns.length;
    const missingAvg = columns.length
      ? columns.reduce((acc, col) => acc + toNumber(col.missing, 0), 0) / columns.length
      : 0;

    metaEl.textContent = `${formatNumber(rows)} rows`;

    metrics.innerHTML = '';
    metrics.append(
      createMetric('rows', formatNumber(rows)),
      createMetric('columns', formatNumber(totalColumns)),
      createMetric('missing', formatPercent(missingAvg, 1))
    );

    const selectedName = next?.data?.selected || columns[0]?.name;
    const selected = columns.find((col) => col.name === selectedName) || columns[0];

    renderHistogram(histSvg, selected?.histogram);

    list.innerHTML = '';
    columns.slice(0, 10).forEach((col) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ew-list-item';
      if (selected && selected.name === col.name) item.classList.add('is-active');

      const head = document.createElement('div');
      head.className = 'ew-list-head';
      const name = document.createElement('span');
      name.className = 'ew-list-title';
      name.textContent = col.name;
      const type = document.createElement('span');
      type.className = 'ew-tag';
      type.textContent = col.type || 'field';
      head.append(name, type);

      const meta = document.createElement('div');
      meta.className = 'ew-list-meta';
      const missing = formatPercent(toNumber(col.missing, 0), 1);
      const distinct = formatNumber(toNumber(col.distinct, 0));
      meta.textContent = `missing ${missing} � distinct ${distinct}`;

      item.append(head, meta);
      item.addEventListener('click', () => {
        api.setData({ selected: col.name });
        api.emit('node:input', { id: node.id, field: 'column', value: col.name });
      });
      list.appendChild(item);
    });
  };

  update(node);
  return { el: root, update };
}

function renderHistogram(svg, values) {
  clearSvg(svg);
  if (!Array.isArray(values) || values.length === 0) {
    return;
  }
  const width = 320;
  const height = 80;
  const pad = 8;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const max = Math.max(...values.map((v) => toNumber(v, 0)), 1);
  const barW = innerW / values.length;

  values.forEach((val, i) => {
    const h = (toNumber(val, 0) / max) * innerH;
    const rect = createSvgEl('rect', {
      x: pad + i * barW + 1,
      y: pad + (innerH - h),
      width: Math.max(2, barW - 2),
      height: h,
      fill: 'var(--ew-ui-accent)',
      'fill-opacity': 0.65
    });
    rect.classList.add('ew-chart-bar');
    svg.appendChild(rect);
  });
}
